import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaStar, FaCalendarAlt } from 'react-icons/fa';
import { getImageUrl } from '../utils/imageHelper';

const CardWrapper = styled(motion.div)`
  position: relative;
  width: ${props => props.$isMobile ? '160px' : '230px'};
  height: ${props => props.$isMobile ? '240px' : '360px'};
  background: #111;
  border-radius: 5px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 8px 25px rgba(0,0,0,0.5);
  flex-shrink: 0;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--sidebar-indicator-color);
  }
`;

const CardImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  background-color: ${props => props.image ? 'transparent' : 'var(--glass-bg)'};
  transition: transform 0.4s ease;

  ${props => !props.image && `
    &::after {
      content: '${props.alt || 'Resim Yok'}';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #1a102a, #0b0614);
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--secondary-color);
      font-size: 0.7rem;
      text-align: center;
      padding: 10px;
      box-sizing: border-box;
    }
  `}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, transparent 40%, rgba(10, 2, 28, 0.95) 100%);
    z-index: 1;
  }

  ${CardWrapper}:hover & {
    transform: scale(1.05);
  }
`;

const HoverOverlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to top, 
    rgba(10, 2, 28, 0.95) 20%, 
    transparent 40%,
    transparent 60%,
    rgba(10, 2, 28, 0.3) 100%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${CardWrapper}:hover & {
    opacity: 1;
  }

  ${props => props.$isMobile && `
    opacity: 1;
    background: linear-gradient(to top, rgba(10, 2, 28, 0.95) 20%, transparent 60%);
  `}
`;

const InfoPanel = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: ${props => props.$isMobile ? '12px' : '20px'};
  color: white;
  z-index: 3;
  
  ${props => !props.$isMobile && `
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.3s ease 0.1s;
    
    ${CardWrapper}:hover & {
      opacity: 1;
      transform: translateY(0);
    }
  `}

  ${props => props.$isMobile && `
    opacity: 1;
    transform: translateY(0);
  `}
`;

const Title = styled.h3`
  font-size: ${props => props.$isMobile ? '0.9rem' : '1.1rem'};
  font-weight: 700;
  margin-bottom: ${props => props.$isMobile ? '4px' : '8px'};
  text-shadow: 0 2px 10px rgba(0,0,0,0.8);
  line-height: 1.2;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${props => props.$isMobile ? '0.7rem' : '0.8rem'};
  color: var(--secondary-color);

  span {
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;
  }
`;

const Rating = styled.span`
  color: #ffc107;
  font-weight: 600;
`;

const TypeTag = styled.div`
  position: absolute;
  top: ${props => props.$isMobile ? '10px' : '12px'};
  right: ${props => props.$isMobile ? '10px' : '12px'};
  background: var(--glass-bg);
  backdrop-filter: blur(10px);
  color: var(--primary-color);
  padding: ${props => props.$isMobile ? '3px 8px' : '4px 10px'};
  border-radius: 6px;
  font-size: ${props => props.$isMobile ? '0.6rem' : '0.7rem'};
  font-weight: 600;
  z-index: 4;
  border: 1px solid var(--border-color);
  opacity: ${props => props.$isMobile ? 1 : 0};
  transform: ${props => props.$isMobile ? 'scale(1)' : 'scale(0.8)'};
  transition: all 0.3s ease;
  
  ${CardWrapper}:hover & {
    opacity: 1;
    transform: scale(1);
  }

  ${props => props.$isMobile && `
    opacity: 1;
    transform: scale(1);
  `}
`;

const EpisodeInfo = styled.div`
  font-size: ${props => props.$isMobile ? '0.6rem' : '0.65rem'};
  color: var(--gradient-cyan);
  margin-top: ${props => props.$isMobile ? '2px' : '4px'};
  font-weight: 500;
`;

const EpisodeBadge = styled.div`
  position: absolute;
  top: ${props => props.$isMobile ? '10px' : '12px'};
  left: ${props => props.$isMobile ? '10px' : '12px'};
  background: var(--gradient-pink);
  color: white;
  padding: ${props => props.$isMobile ? '2px 6px' : '3px 8px'};
  border-radius: 4px;
  font-size: ${props => props.$isMobile ? '0.55rem' : '0.6rem'};
  font-weight: 600;
  z-index: 4;
  opacity: ${props => props.$isMobile ? 1 : 0};
  transform: ${props => props.$isMobile ? 'scale(1)' : 'scale(0.8)'};
  transition: all 0.3s ease;
  
  ${CardWrapper}:hover & {
    opacity: 1;
    transform: scale(1);
  }

  ${props => props.$isMobile && `
    opacity: 1;
    transform: scale(1);
  `}
`;

const GlassCard = ({ 
  title, 
  imageUrl, 
  rating, 
  releaseDate, 
  mediaTypeLabel, 
  lastEpisodeInfo, 
  isMobile = false,
  episodeInfo,
  episodeTitle,
  episodeDate,
  linkTo
}) => {
  const safeRating =
    typeof rating === 'number'
      ? rating
      : typeof rating === 'string'
      ? parseFloat(rating)
      : 0;

  const formattedDate = releaseDate
    ? new Date(releaseDate).getFullYear()
    : 'N/A';

  // ✅ Proxy kontrolü - eğer proxy URL değilse proxy ekle
  const safeImageUrl = imageUrl 
    ? (imageUrl.includes('/proxy-image.php') 
        ? imageUrl 
        : getImageUrl(imageUrl))
    : '/placeholder-image.jpg';

  const CardContent = (
    <CardWrapper 
      $isMobile={isMobile}
      whileHover={!isMobile ? { y: -5 } : {}}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <CardImage image={safeImageUrl} alt={title} />

      <HoverOverlay $isMobile={isMobile} />
      
      {mediaTypeLabel && <TypeTag $isMobile={isMobile}>{mediaTypeLabel}</TypeTag>}
      {episodeInfo && <EpisodeBadge $isMobile={isMobile}>{episodeInfo}</EpisodeBadge>}

      <InfoPanel $isMobile={isMobile}>
        <Title $isMobile={isMobile}>{title}</Title>

        {episodeTitle && (
          <EpisodeInfo $isMobile={isMobile} style={{ color: 'var(--gradient-cyan)', marginBottom: '4px' }}>
            {episodeTitle}
          </EpisodeInfo>
        )}

        <MetaInfo $isMobile={isMobile}>
          {safeRating > 0 && (
            <Rating>
              <FaStar /> {safeRating.toFixed(1)}
            </Rating>
          )}
          {releaseDate && (
            <span>
              {formattedDate} <FaCalendarAlt />
            </span>
          )}
        </MetaInfo>

        {episodeDate && (
          <EpisodeInfo $isMobile={isMobile} style={{ color: 'var(--secondary-color)', marginTop: '2px' }}>
            {episodeDate}
          </EpisodeInfo>
        )}

        {lastEpisodeInfo && (
          <EpisodeInfo $isMobile={isMobile}>{lastEpisodeInfo}</EpisodeInfo>
        )}
      </InfoPanel>
    </CardWrapper>
  );

  return linkTo ? (
    <Link to={linkTo} style={{ textDecoration: 'none', color: 'inherit' }}>
      {CardContent}
    </Link>
  ) : CardContent;
};

export default GlassCard;
